import DOMPurify from 'dompurify';
import { marked } from 'marked';

const DEFAULT_ALLOWED_TAGS = [
  'a',
  'abbr',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const DEFAULT_ALLOWED_ATTR = [
  'class',
  'colspan',
  'data-comment-id',
  'data-tag',
  'data-username',
  'href',
  'rel',
  'rowspan',
  'target',
  'title',
];

const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];
const ALLOWED_EMBED_HOSTS = new Set([
  'player.vimeo.com',
  'vimeo.com',
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
]);

const normalizeHtml = (value) => String(value || '').trim();

const sanitizeAnchors = (html) => {
  if (!html || typeof window === 'undefined') {
    return html;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstElementChild;

  if (!container) {
    return '';
  }

  container.querySelectorAll('a').forEach((anchor) => {
    const href = (anchor.getAttribute('href') || '').trim();
    if (!href) {
      anchor.removeAttribute('href');
      return;
    }

    try {
      const parsed = new URL(href, window.location.origin);
      if (!ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol)) {
        anchor.removeAttribute('href');
      }
    } catch (_error) {
      anchor.removeAttribute('href');
    }

    anchor.setAttribute('rel', 'noopener noreferrer');
    anchor.setAttribute('target', '_blank');
  });

  return container.innerHTML;
};

export const sanitizeHtml = (
  html,
  {
    allowedTags = DEFAULT_ALLOWED_TAGS,
    allowedAttr = DEFAULT_ALLOWED_ATTR,
    forbidAttr = ['style'],
  } = {}
) => {
  const normalizedHtml = normalizeHtml(html);
  if (!normalizedHtml) {
    return '';
  }

  const sanitized = DOMPurify.sanitize(normalizedHtml, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
    FORBID_TAGS: ['form', 'iframe', 'input', 'math', 'object', 'script', 'style', 'svg'],
    FORBID_ATTR: forbidAttr,
    ALLOW_DATA_ATTR: false,
  });

  return sanitizeAnchors(sanitized);
};

export const sanitizeHighlightedHtml = (html) =>
  sanitizeHtml(html, {
    allowedAttr: [...DEFAULT_ALLOWED_ATTR, 'data-tag', 'data-username'],
  });

export const renderMarkdownToSafeHtml = (input) => {
  const normalizedInput = normalizeHtml(input);
  if (!normalizedInput) {
    return '';
  }

  let text = normalizedInput.replace(/\r\n/g, '\n');
  text = text.replace(/\n[\t ]*[-•]\s/g, '\n* ');
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  return sanitizeHtml(marked.parse(text));
};

export const sanitizeTrustedVideoEmbed = (embedCode) => {
  const normalizedHtml = normalizeHtml(embedCode);
  if (!normalizedHtml || typeof window === 'undefined') {
    return '';
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(normalizedHtml, 'text/html');
  const iframe = doc.querySelector('iframe');

  if (!iframe || doc.body.childElementCount !== 1) {
    return '';
  }

  const src = iframe.getAttribute('src');
  if (!src) {
    return '';
  }

  try {
    const parsed = new URL(src, window.location.origin);
    if (!ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol)) {
      return '';
    }
    if (!ALLOWED_EMBED_HOSTS.has(parsed.hostname)) {
      return '';
    }
  } catch (_error) {
    return '';
  }

  const sanitized = DOMPurify.sanitize(iframe.outerHTML, {
    ALLOWED_TAGS: ['iframe'],
    ALLOWED_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'height',
      'loading',
      'referrerpolicy',
      'src',
      'title',
      'width',
    ],
    FORBID_ATTR: ['style'],
  });

  return sanitized;
};
